import { injectable } from 'inversify';
import { action, makeObservable, observable, runInAction } from 'mobx';
import type { Logger } from 'loglevel';
import { Subscription, switchMap, takeWhile, timer } from 'rxjs';

import { DizraptorKycState } from '@gql/types.generated';
import { GqlService } from '@services/GqlService';
import { subscribeUniversal } from '@utils/subscribeUniversal';
import { iocNames } from '../../iocNames';
import { GetKycStatusDataDocument, GetKycStatusDataQuery } from './getKYCStatus.generated';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
iocNames;

type Level = 0 | 1 | 2;

export interface KYCStatus {
  currentLevel?: Level;
  nextLevelStatusProgress?: DizraptorKycState;
  updateKYCStatus: () => Promise<
    | void
    | {
        currentLevel: Level;
        nextLevelStatusProgress:
          | undefined
          | DizraptorKycState.Failed
          | DizraptorKycState.FinalFailed
          | DizraptorKycState.NotRequested
          | DizraptorKycState.Requested;
      }
    | undefined
  >;
  isLoading: boolean;
  error: unknown;
}

@injectable()
export class KYCStatusStore implements KYCStatus {
  @observable
  currentLevel?: 0 | 1 | 2;

  @observable
  nextLevelStatusProgress?: DizraptorKycState;

  @observable
  public isLoading = false;

  @observable
  public error: unknown;

  constructor(private gqlService: GqlService, logger: Logger) {
    makeObservable(this);
    let subs: Subscription;

    subscribeUniversal(
      this,
      ['currentLevel', 'nextLevelStatusProgress', 'isLoading', 'error'],
      () => {
        subs = timer(0, 10000)
          .pipe(
            switchMap(() => {
              return this.updateKYCStatus();
            }),
            takeWhile(kycInfo => kycInfo?.currentLevel !== 2)
          )
          .subscribe({
            error: err => {
              logger.error(err);
            }
          });
      },
      () => {
        subs.unsubscribe();
        this.currentLevel = undefined;
        this.nextLevelStatusProgress = undefined;
      }
    );
  }

  @action
  public updateKYCStatus = () => {
    this.isLoading = true;
    this.error = undefined;

    return this.gqlService
      .makeRequest({
        requestOptions: {
          document: GetKycStatusDataDocument
        },
        needAuthorization: true,
        shouldSetEnvAutomatically: false
      })
      .then(result => {
        const processingResult = this.processKYCStatus(result);

        runInAction(() => {
          this.currentLevel = processingResult?.currentLevel;
          this.nextLevelStatusProgress = processingResult?.nextLevelStatusProgress;
        });

        return processingResult;
      })
      .catch(err => {
        runInAction(() => {
          this.error = err;
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };

  private processKYCStatus(result: GetKycStatusDataQuery) {
    const level = result.dizraptorCompliance?.kyc.kycLevel;
    if (!level) {
      return;
    }

    if (level.level2SelfCertificated === DizraptorKycState.Confirmed) {
      return {
        currentLevel: 2 as const,
        nextLevelStatusProgress: undefined
      };
    }

    if (level.level1Validated === DizraptorKycState.Confirmed) {
      return {
        currentLevel: 1 as const,
        nextLevelStatusProgress: level.level2SelfCertificated
      };
    }

    return {
      currentLevel: 0 as const,
      nextLevelStatusProgress: level.level1Validated
    };
  }
}
